import React, { useState } from "react";
import { Card, Button, Space, message, Modal, Alert, Table, Input } from "antd";
import _ from "lodash";
import { PostRequestResolve } from "utils/FetchHelper";
import URL from "utils/url";
import CustomButton from "../../../../../common/CustomButton/Button";
import Title from "antd/es/typography/Title";
import SccPropertiesView from "./SccPropertiesView";


// SCCHelper Summary React Functional Component
function SCCHelper({ acctDetails }) {
    const acctName = _.get(acctDetails, "Name", "");

    // Metadata state
    const [metadata, setMetadata] = useState(_.get(acctDetails, "AccountProperties.provisioningInfo.additionalPiamMetadata", []));
    const [metadataEditMode, setMetadataEditMode] = useState(false);
    const [editedMetadata, setEditedMetadata] = useState([]);

    // Entitlement history state
    const [entitlementHistory, setEntitlementHistory] = useState([]);
    const [loadingEntitlementHistory, setLoadingEntitlementHistory] = useState(false);

    // MCD Context state
    const [tenantContext, setTenantContext] = useState(null);
    const [loadingTenantContext, setLoadingTenantContext] = useState(false);
    const [tenantContextFetched, setTenantContextFetched] = useState(false);


    // fetch entitlement history
    const fetchEntitlementHistory = () => {
        setLoadingEntitlementHistory(true);
        const payload = {
            acctName: acctName,
        };
        PostRequestResolve(URL.getSccEntitlementHistory, null, payload)
            .then((response) => {
                setEntitlementHistory(response);
            })
            .catch(() => {
                message.error("Failed to fetch entitlement history");
            }).finally(() => {
                setLoadingEntitlementHistory(false);
            }
            );
    };
    
     // Fetch tenant context from CDO
     const fetchTenantContext = () => {
        setLoadingTenantContext(true);
        const payload = {
            name: acctName,
        };
        PostRequestResolve(URL.getCDOTenantContext, null, payload)
            .then((response) => {
                setTenantContext(response);
                setTenantContextFetched(true);
            })
            .catch((err) => {
                console.error("Error fetching tenant context:", err);
                setTenantContext(null);
                setTenantContextFetched(true);
            })
            .finally(() => {
                setLoadingTenantContext(false);
            });
    };

    // Fetch entitlement history on component mount
    React.useEffect(() => {
        if (acctName) {
            fetchEntitlementHistory();
            // Fetch tenant context if enterprise_id exists
            const enterpriseId = _.get(acctDetails, "AccountProperties.sccProperties.enterprise_id", "");
            if (enterpriseId !== "") {
                fetchTenantContext();
            }
        }
    }, [acctName]);

    const handleUpdateAccountSettingInSCC = () => {
        Modal.confirm({
            title: "Sync Navigation Properties",
            content: "Are you sure you want to sync navigation properties? This may overwrite existing settings.",
            okText: "Yes",
            cancelText: "No",
            onOk: () => {
                const payload = {
                    name: acctName,
                };
                PostRequestResolve(URL.updateAccountSettingInSCC, null, payload)
                    .then(() => {
                        message.success("Account settings updated in SCC successfully");
                    })
                    .catch((err) => {
                        message.error("Failed to update account settings in SCC: " + _.get(err, "message", "Unknown error"));
                        console.error("Error updating account settings in SCC:", err);
                    });
            }
        });
    };

    const handleFetchPIAMProperties = () => {
        Modal.confirm({
            title: "Fetch PIAM Properties",
            content: "Are you sure you want to fetch PIAM properties? This will fetch the latest properties from PIAM.",
            okText: "Yes",
            cancelText: "No",
            onOk: () => {
                const payload = {
                    name: acctName,
                };
                PostRequestResolve(URL.fetchPIAMProperties, null, payload)
                    .then((response) => {
                        message.success("PIAM properties fetched successfully, Reload the page to see the changes");
                        setMetadata(_.get(response, "additionalTenantMetadata", {}));
                        setEditedMetadata(_.get(response, "additionalTenantMetadata", {}));
                    })
                    .catch((err) => {
                        message.error("Failed to fetch PIAM properties:" + _.get(err, "message", "Unknown error"));
                        console.error("Error fetching PIAM properties:", err);
                    });
            }
        });
    };

    // Metadata edit handlers
    const handleMetadataEdit = () => {
        setMetadataEditMode(true);
        setEditedMetadata(metadata);
    };

    const handleMetadataCancel = () => {
        setMetadataEditMode(false);
        setEditedMetadata(metadata);
    };

    const handleMetadataSave = () => {
        Modal.confirm({
            title: "Save PIAM Metadata",
            content: "Are you sure you want to save the updated PIAM metadata?",
            okText: "Yes",
            cancelText: "No",
            onOk: () => {
                const payload = {
                    name: acctName,
                    additionalTenantMetadata: editedMetadata,
                };
                PostRequestResolve(URL.updatePIAMMetadata, null, payload)
                    .then(() => {
                        message.success("Metadata updated successfully");
                        setMetadata(editedMetadata);
                        setMetadataEditMode(false);
                    })
                    .catch((err) => {
                        message.error("Failed to update metadata: " + _.get(err, "message", "Unknown error"));
                        console.error("Error updating metadata:", err);
                    });
            }
        });
    };

    const handleSendCallbackToPIAM = () => {
        Modal.confirm({
            title: "Send Callback to PIAM",
            content: "Are you sure you want to send a callback to PIAM? This action may trigger provisioning or sync operations.",
            okText: "Yes",
            cancelText: "No",
            onOk: () => {
                const payload = {
                    name: acctName,
                };
                PostRequestResolve(URL.sendCallbackToPIAM, null, payload)
                    .then(() => {
                        message.success("Account settings updated in SCC successfully");
                    })
                    .catch((err) => {
                        message.error("Failed to update account settings in SCC: " + _.get(err, "message", "Unknown error"));
                        console.error("Error updating account settings in SCC:", err);
                    });
            }
        });
    }

    const handleUpdateMcdContextInCdo = () => {
        Modal.confirm({
            title: "Enable MCD Context in CDO",
            content: "Are you sure you want to enable MCD in CDO for this account? This action cannot be undone.",
            okText: "Enable",
            cancelText: "Cancel",
            onOk: () => {
                const payload = {
                    name: acctName,
                };
                PostRequestResolve(URL.enableMCDInCDOTenantContext, null, payload)
                    .then(() => {
                        message.success("MCD enabled in CDO successfully");
                        // Refresh tenant context to update the UI
                        fetchTenantContext();
                    })
                    .catch((err) => {
                        message.error("Failed to enable MCD in CDO: " + _.get(err, "message", "Unknown error"));
                        console.error("Error enabling MCD in CDO:", err);
                    });
            }
        });
    }

    return (
        <>
            <Space style={{ marginBottom: 16 }} gap={8}>
                {_.get(acctDetails, "AccountProperties.sccProperties.processing_meta.callback_url", "") !== "" && (
                    <CustomButton onClick={handleSendCallbackToPIAM} enableOnlyForAdmin>
                        Send Callback to PIAM
                    </CustomButton>
                )}
                {_.get(acctDetails, "AccountProperties.sccProperties.enterprise_id", "") !== "" && (
                    <>
                        <CustomButton onClick={handleUpdateAccountSettingInSCC} enableOnlyForAdmin>
                            Send Navigation Properties
                        </CustomButton>
                        <CustomButton onClick={handleFetchPIAMProperties} enableOnlyForAdmin>
                            Fetch Product from PIAM
                        </CustomButton>
                    </>
                )}
            </Space>
            <SccPropertiesView acctDetails={acctDetails} />
            <Card
                size="small"
                title="PIAM Metadata"
                style={{ marginTop: 16 }}
                extra={
                    metadataEditMode ? (
                        <Space>
                            <Button size="small" onClick={handleMetadataCancel}>Cancel</Button>
                            <Button type="primary" size="small" onClick={handleMetadataSave}>Save</Button>
                            <Button size="small" type="dashed" onClick={() => setEditedMetadata([...editedMetadata, { key: "", value: "" }])}>
                                Add Row
                            </Button>
                        </Space>
                    ) : (
                        <CustomButton size="small" onClick={handleMetadataEdit} enableOnlyForAdmin>Edit</CustomButton>
                    )
                }
            >
                {_.get(acctDetails, "AccountProperties.sccProperties.product_instance_id", "") !== "" ? (
                    <Table
                        dataSource={metadataEditMode ? editedMetadata : metadata}
                        rowKey={(idx) => idx}
                        pagination={false}
                        size="small"
                        columns={[
                            {
                                title: "Key",
                                dataIndex: "key",
                                render: (text, record, idx) =>
                                    metadataEditMode ? (
                                        <Input
                                            value={text}
                                            onChange={e => {
                                                const newData = [...editedMetadata];
                                                newData[idx].key = e.target.value;
                                                setEditedMetadata(newData);
                                            }}
                                        />
                                    ) : (
                                        text
                                    )
                            },
                            {
                                title: "Value",
                                dataIndex: "value",
                                render: (text, record, idx) =>
                                    metadataEditMode ? (
                                        <Input
                                            value={text}
                                            onChange={e => {
                                                const newData = [...editedMetadata];
                                                newData[idx].value = e.target.value;
                                                setEditedMetadata(newData);
                                            }}
                                        />
                                    ) : (
                                        text
                                    )
                            },
                            ...(metadataEditMode
                                ? [{
                                    title: "Action",
                                    dataIndex: "action",
                                    render: (_, __, idx) => (
                                        <Button
                                            danger
                                            size="small"
                                            onClick={() => {
                                                const newData = [...editedMetadata];
                                                newData.splice(idx, 1);
                                                setEditedMetadata(newData);
                                            }}
                                        >
                                            Remove
                                        </Button>
                                    )
                                }]
                                : [])
                        ]}
                    />
                ) : (
                    <Alert
                        message="PIAM Metadata can't be set for SCC accounts without product instance id"
                    />
                )}
            </Card>
            <Title level={4} style={{ marginTop: 16 }}>
                SCC Entitlement History
                <Button
                    type="primary"
                    size="small"
                    style={{ marginLeft: 8 }}
                    onClick={fetchEntitlementHistory}
                    disabled={loadingEntitlementHistory}
                >
                    Refresh History
                </Button>
            </Title>
            {loadingEntitlementHistory && <Alert message="Loading entitlement history..." type="info" />}
            {!loadingEntitlementHistory && entitlementHistory.length === 0 && (
                <Alert message="No entitlement history found" type="info" />
            )}
            {!loadingEntitlementHistory && _.map(entitlementHistory, (history) => (
                <Card
                    key={_.get(history, "ID", "")}
                    size="small"
                    style={{ marginBottom: 8 }}
                    title={<div><span>ID: {_.get(history, "ID", "")}</span>&nbsp;<span className="gray-5">({_.get(history, "CreatedAt", "")})</span></div>}
                >
                    {_.size(_.get(history, "Properties.entitlement", [])) === 0 ? (
                        <Alert message="No entitlement properties found" type="warning" />
                    ) : (
                        <Table
                            dataSource={_.get(history, "Properties.entitlement", [])}
                            rowKey={(record) => record.name}
                            pagination={false}
                            size="small"
                            columns={[
                                {
                                    title: "Name",
                                    dataIndex: "name",
                                    key: "name",
                                },
                                {
                                    title: "Value",
                                    dataIndex: "value",
                                    key: "value",
                                },
                                {
                                    title: "Unit",
                                    dataIndex: ["quantity", "unit"],
                                    key: "quantity",
                                },
                                {
                                    title: "Quantity",
                                    dataIndex: ["quantity", "value"],
                                    key: "quantity",
                                },
                                {
                                    title: "Enforce Quantity",
                                    dataIndex: "enforce_quantity",
                                    key: "enforce_quantity",
                                    render: (text) => (text ? "Yes" : "No")
                                }
                            ]}
                        />
                    )}
                </Card>
            ))}
        </>
    );
}

export default SCCHelper;